import { useEffect, useRef, useState, useCallback } from "react";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Clock, LogIn, LogOut, Navigation, Coffee, Play, Loader2 } from "lucide-react";
import { toast } from "sonner";

const GEO_OPTS = { enableHighAccuracy: true, timeout: 15000, maximumAge: 5000 };
const MOVING_MPS = 1.5;

const getPos = () => new Promise((res) => {
  if (!navigator.geolocation) return res({ error: "UNAVAILABLE" });
  navigator.geolocation.getCurrentPosition((p) => res({ coords: p.coords }),
    (e) => res({ error: e.code === 1 ? "DENIED" : "UNAVAILABLE" }), GEO_OPTS);
});

const TRACK_TEXT = {
  starting: ["Starting location tracking…", "text-muted-foreground"],
  denied: ["Location permission denied — tracking is NOT active. Allow location for this site in your browser settings. Dispatch has been alerted.", "text-danger"],
  unavailable: ["Location unavailable on this device — tracking is NOT active.", "text-danger"],
  offline: ["No connection — location updates are not reaching dispatch.", "text-danger"],
  failing: ["Location updates are failing — tracking not confirmed.", "text-danger"],
  signed_out: ["Signed out — your sign-in expired. Sign in again to resume tracking. Your shift is still open.", "text-danger"],
  stale: ["No confirmed location update recently — tracking not confirmed.", "text-ochre"],
};

export const WorkSessionCard = ({ vehicleId, activeTripId }) => {
  const [session, setSession] = useState(undefined);
  const [policy, setPolicy] = useState({ moving_interval_s: 15, parked_interval_s: 60, stale_after_s: 60 });
  const [track, setTrack] = useState("starting");
  const [lastSent, setLastSent] = useState(null);
  const [stats, setStats] = useState({ sent: 0, failed: 0 });
  const [busy, setBusy] = useState(false);
  const [, setTick] = useState(0);
  const lastRef = useRef(0);
  const movingRef = useRef(false);
  const tripRef = useRef(activeTripId);
  tripRef.current = activeTripId;
  const sessionId = session?.id;

  const load = useCallback(() => api.get("/driver/work-sessions/current").then((r) => setSession(r.data.session))
    .catch((e) => { if (e?.response?.status === 401) setTrack("signed_out"); setSession(null); }), []);
  useEffect(() => { load(); api.get("/tracking/policy").then((r) => setPolicy(r.data)).catch(() => {}); }, [load]);

  const report = useCallback((state, message) => {
    if (sessionId) api.post("/driver/tracking-status", { state, message, session_id: sessionId }).catch(() => {});
  }, [sessionId]);

  // App-managed tracking runs for the whole active session (working or on break).
  useEffect(() => {
    if (!sessionId) return;
    setTrack("starting"); setLastSent(null); lastRef.current = 0;
    if (!navigator.geolocation) { setTrack("unavailable"); report("UNAVAILABLE", "Geolocation not supported"); return; }
    let stopped = false;
    const intervalMs = () => 1000 * (movingRef.current ? policy.moving_interval_s : policy.parked_interval_s);
    const send = (p, force) => {
      if (stopped) return;
      movingRef.current = (p.coords.speed || 0) > MOVING_MPS;
      if (!force && Date.now() - lastRef.current < intervalMs()) return;
      lastRef.current = Date.now();
      const c = p.coords;
      api.post("/driver/location", { session_id: sessionId, lat: c.latitude, lng: c.longitude, accuracy: c.accuracy, speed: c.speed, heading: c.heading, recorded_at: new Date(p.timestamp).toISOString(), trip_id: tripRef.current || undefined })
        .then(() => { setLastSent(new Date()); setTrack("active"); setStats((s) => ({ ...s, sent: s.sent + 1 })); })
        .catch((e) => {
          const code = e?.response?.status;
          if (code === 429) return;
          lastRef.current = 0;
          setStats((s) => ({ ...s, failed: s.failed + 1 }));
          if (code === 401) { setTrack("signed_out"); return; }
          if (code === 409 || code === 400) { setTrack("failing"); load(); return; }
          setTrack(navigator.onLine ? "failing" : "offline");
        });
    };
    const onErr = (err) => { const s = err.code === 1 ? "denied" : "unavailable"; setTrack(s); report(s.toUpperCase(), err.message); };
    const id = navigator.geolocation.watchPosition((p) => send(p, false), onErr, GEO_OPTS);
    // Heartbeat: watchPosition may not fire while parked.
    const hb = setInterval(() => {
      setTick((n) => n + 1);
      if (Date.now() - lastRef.current >= intervalMs()) navigator.geolocation.getCurrentPosition((p) => send(p, true), onErr, GEO_OPTS);
    }, 5000);
    const off = () => setTrack("offline");
    const on = () => { lastRef.current = 0; setTrack("starting"); };
    window.addEventListener("offline", off); window.addEventListener("online", on);
    return () => { stopped = true; navigator.geolocation.clearWatch(id); clearInterval(hb); window.removeEventListener("offline", off); window.removeEventListener("online", on); };
  }, [sessionId, report, load, policy.moving_interval_s, policy.parked_interval_s]);

  const act = async (fn) => { setBusy(true); try { await fn(); } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); } };
  const clockIn = () => act(async () => {
    const pos = await getPos();
    const c = pos.coords;
    await api.post("/driver/work-sessions/clock-in", { vehicle_id: vehicleId, ...(c ? { lat: c.latitude, lng: c.longitude, accuracy: c.accuracy } : {}) });
    if (pos.error === "DENIED") toast.warning("Clocked in, but location permission was denied — tracking is not active.");
    else if (pos.error) toast.warning("Clocked in, but location is unavailable — tracking is not active.");
    else toast.success("Clocked in");
    setStats({ sent: 0, failed: 0 });
    await load();
  });
  const startBreak = () => act(async () => { const { data } = await api.post("/driver/work-sessions/break/start"); setSession(data.session); toast.success("On break — dispatch notified"); });
  const endBreak = () => act(async () => { const { data } = await api.post("/driver/work-sessions/break/end"); setSession(data.session); toast.success("Back to work — dispatch notified"); });
  const clockOut = () => act(async () => {
    const pos = await getPos();
    const c = pos.coords;
    const { data } = await api.post("/driver/work-sessions/clock-out", c ? { lat: c.latitude, lng: c.longitude, accuracy: c.accuracy } : {});
    toast.success(`Clocked out · ${data.duration_minutes} min`); await load();
  });

  if (session === undefined) return null;
  const onBreak = session?.duty_status === "ON_BREAK";
  const staleMs = 1000 * Math.max(policy.stale_after_s, policy.parked_interval_s * 2);
  const stale = track === "active" && lastSent && Date.now() - lastSent.getTime() > staleMs;
  const [msg, cls] = track === "active" && !stale
    ? [`Location tracking active · last confirmed ${lastSent.toLocaleTimeString()}`, "text-success"]
    : TRACK_TEXT[stale ? "stale" : track];

  return (
    <div className="border border-border rounded-lg bg-card p-5 mb-5" data-testid="work-session-card">
      <div className="flex items-center gap-2 mb-3"><Clock className="w-4 h-4 text-brand" /><span className="text-xs uppercase tracking-wider text-muted-foreground">Work Session</span></div>
      {session ? (
        <>
          <div className="flex items-center justify-between gap-2">
            <p className="font-semibold">On duty since {new Date(session.clock_in_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</p>
            <span className={`text-[11px] font-bold rounded-full px-2.5 py-1 ${onBreak ? "bg-ochre/15 text-ochre" : "bg-success/15 text-success"}`} data-testid="work-session-status">{onBreak ? "ON BREAK" : "WORKING"}</span>
          </div>
          <p className={`text-xs mt-2 flex items-start gap-1 ${cls}`} data-testid="location-tracking-status" data-state={stale ? "stale" : track}>
            <Navigation className="w-3.5 h-3.5 mt-px shrink-0" /> {msg}
          </p>
          <p className="text-[11px] text-muted-foreground mt-1" data-testid="tracking-counters">Updates sent: {stats.sent} · failed: {stats.failed} · every {policy.moving_interval_s}s moving / {policy.parked_interval_s}s parked</p>
          <p className="text-[11px] text-muted-foreground mt-1">Your location is shared with dispatch only during your shift. Keep this page open — browsers can pause location when the app is in the background or the screen is locked.</p>
          <div className="grid grid-cols-2 gap-2 mt-4">
            {onBreak
              ? <Button variant="outline" className="h-12" onClick={endBreak} disabled={busy} data-testid="resume-work-btn"><Play className="w-4 h-4 mr-1.5" /> Resume Work</Button>
              : <Button variant="outline" className="h-12" onClick={startBreak} disabled={busy} data-testid="on-break-btn"><Coffee className="w-4 h-4 mr-1.5" /> On Break</Button>}
            <Button className="h-12 bg-danger hover:bg-danger/90" onClick={clockOut} disabled={busy} data-testid="clock-out-btn">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><LogOut className="w-4 h-4 mr-1.5" /> Clock Out</>}</Button>
          </div>
        </>
      ) : (
        <>
          {track === "signed_out" && <p className="text-xs text-danger mb-2" data-testid="signed-out-msg">{TRACK_TEXT.signed_out[0]}</p>}
          <p className="text-sm text-muted-foreground">You're off duty. Location tracking starts when you clock in and stops when you clock out.</p>
          <Button className="w-full h-12 mt-4" onClick={clockIn} disabled={busy} data-testid="clock-in-btn">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><LogIn className="w-4 h-4 mr-1.5" /> Clock In</>}</Button>
        </>
      )}
    </div>
  );
};
